import { type Locale as DateLocale, setDefaultOptions } from "date-fns";
import i18n, { type ReadCallback } from "i18next";
import { initReactI18next } from "react-i18next";
import { z } from "zod";
import { makeZodI18nMap } from "zod-i18n-map";

import { type Locale, locales } from "@/constants/locale";

export let locale = locales[import.meta.env.VITE_DEFAULT_LOCALE];

const updateDateLocale = (lng: string) => {
  const localeMaps: { [key: string]: () => Promise<DateLocale> } = {
    fa: () => import("date-fns/locale/fa-IR").then(({ faIR }) => faIR),
    en: () => import("date-fns/locale/en-US").then(({ enUS }) => enUS),
    fr: () => import("date-fns/locale/fr").then(({ fr }) => fr),
    de: () => import("date-fns/locale/de").then(({ de }) => de),
    es: () => import("date-fns/locale/es").then(({ es }) => es),
    it: () => import("date-fns/locale/it").then(({ it }) => it),
    ar: () => import("date-fns/locale/ar").then(({ ar }) => ar),
  };

  return localeMaps[lng]().then((locale) => {
    setDefaultOptions({ locale });
  });
};

export function setLocale(newLocale: Locale) {
  locale = locales[newLocale] || locales[import.meta.env.VITE_DEFAULT_LOCALE];
  i18n.changeLanguage(locale.key);
  updateDateLocale(locale.key);

  document.documentElement.setAttribute("dir", locale.isRtl ? "rtl" : "ltr");
  document.documentElement.setAttribute("lang", locale.key);
}

const translations: { [key: string]: Record<string, () => Promise<any>> } = {
  fa: {
    // Core translations (always present)
    "core.zod": () => import("../locales/fa/zod.json"),
    "core.user": () => import("../locales/fa/user.json"),
    "core.shared": () => import("../locales/fa/shared.json"),
    "core.admin": () => import("../locales/fa/admin.json"),
  },
  en: {
    // Core English translations
    "core.zod": () => import("../locales/en/zod.json"),
    "core.user": () => import("../locales/en/user.json"),
    "core.shared": () => import("../locales/en/shared.json"),
    "core.admin": () => import("../locales/en/admin.json"),
  },
  fr: {
    // Core French translations
    "core.zod": () => import("../locales/fr/zod.json"),
    "core.user": () => import("../locales/fr/user.json"),
    "core.shared": () => import("../locales/fr/shared.json"),
    "core.admin": () => import("../locales/fr/admin.json"),
  },
  de: {
    // Core German translations
    "core.zod": () => import("../locales/de/zod.json"),
    "core.user": () => import("../locales/de/user.json"),
    "core.shared": () => import("../locales/de/shared.json"),
    "core.admin": () => import("../locales/de/admin.json"),
  },
  es: {
    // Core Spanish translations
    "core.zod": () => import("../locales/es/zod.json"),
    "core.user": () => import("../locales/es/user.json"),
    "core.shared": () => import("../locales/es/shared.json"),
    "core.admin": () => import("../locales/es/admin.json"),
  },
  it: {
    // Core Italian translations
    "core.zod": () => import("../locales/it/zod.json"),
    "core.user": () => import("../locales/it/user.json"),
    "core.shared": () => import("../locales/it/shared.json"),
    "core.admin": () => import("../locales/it/admin.json"),
  },
  ar: {
    // Core Arabic translations
    "core.zod": () => import("../locales/ar/zod.json"),
    "core.user": () => import("../locales/ar/user.json"),
    "core.shared": () => import("../locales/ar/shared.json"),
    "core.admin": () => import("../locales/ar/admin.json"),
  },
};

// Conditionally add profile module
if (import.meta.env.__HAS_PROFILE__) {
  translations.fa["profile.user"] = () =>
    import("../../modules/profile/locales/fa/user.json");
  translations.fa["profile.shared"] = () =>
    import("../../modules/profile/locales/fa/shared.json");
  translations.fa["profile.admin"] = () =>
    import("../../modules/profile/locales/fa/admin.json");

  translations.en["profile.user"] = () =>
    import("../../modules/profile/locales/en/user.json");
  translations.en["profile.shared"] = () =>
    import("../../modules/profile/locales/en/shared.json");
  translations.en["profile.admin"] = () =>
    import("../../modules/profile/locales/en/admin.json");

  translations.fr["profile.user"] = () =>
    import("../../modules/profile/locales/fr/user.json");
  translations.fr["profile.shared"] = () =>
    import("../../modules/profile/locales/fr/shared.json");
  translations.fr["profile.admin"] = () =>
    import("../../modules/profile/locales/fr/admin.json");

  translations.de["profile.user"] = () =>
    import("../../modules/profile/locales/de/user.json");
  translations.de["profile.shared"] = () =>
    import("../../modules/profile/locales/de/shared.json");
  translations.de["profile.admin"] = () =>
    import("../../modules/profile/locales/de/admin.json");

  translations.es["profile.user"] = () =>
    import("../../modules/profile/locales/es/user.json");
  translations.es["profile.shared"] = () =>
    import("../../modules/profile/locales/es/shared.json");
  translations.es["profile.admin"] = () =>
    import("../../modules/profile/locales/es/admin.json");

  translations.it["profile.user"] = () =>
    import("../../modules/profile/locales/it/user.json");
  translations.it["profile.shared"] = () =>
    import("../../modules/profile/locales/it/shared.json");
  translations.it["profile.admin"] = () =>
    import("../../modules/profile/locales/it/admin.json");

  translations.ar["profile.user"] = () =>
    import("../../modules/profile/locales/ar/user.json");
  translations.ar["profile.shared"] = () =>
    import("../../modules/profile/locales/ar/shared.json");
  translations.ar["profile.admin"] = () =>
    import("../../modules/profile/locales/ar/admin.json");
}

// Conditionally add recognition module
if (import.meta.env.__HAS_RECOGNITION__) {
  translations.fa["recognition.user"] = () =>
    import("../../modules/recognition/locales/fa/user.json");
  translations.fa["recognition.shared"] = () =>
    import("../../modules/recognition/locales/fa/shared.json");
  translations.fa["recognition.admin"] = () =>
    import("../../modules/recognition/locales/fa/admin.json");

  translations.en["recognition.user"] = () =>
    import("../../modules/recognition/locales/en/user.json");
  translations.en["recognition.shared"] = () =>
    import("../../modules/recognition/locales/en/shared.json");
  translations.en["recognition.admin"] = () =>
    import("../../modules/recognition/locales/en/admin.json");

  translations.fr["recognition.user"] = () =>
    import("../../modules/recognition/locales/fr/user.json");
  translations.fr["recognition.shared"] = () =>
    import("../../modules/recognition/locales/fr/shared.json");
  translations.fr["recognition.admin"] = () =>
    import("../../modules/recognition/locales/fr/admin.json");

  translations.de["recognition.user"] = () =>
    import("../../modules/recognition/locales/de/user.json");
  translations.de["recognition.shared"] = () =>
    import("../../modules/recognition/locales/de/shared.json");
  translations.de["recognition.admin"] = () =>
    import("../../modules/recognition/locales/de/admin.json");

  translations.es["recognition.user"] = () =>
    import("../../modules/recognition/locales/es/user.json");
  translations.es["recognition.shared"] = () =>
    import("../../modules/recognition/locales/es/shared.json");
  translations.es["recognition.admin"] = () =>
    import("../../modules/recognition/locales/es/admin.json");

  translations.it["recognition.user"] = () =>
    import("../../modules/recognition/locales/it/user.json");
  translations.it["recognition.shared"] = () =>
    import("../../modules/recognition/locales/it/shared.json");
  translations.it["recognition.admin"] = () =>
    import("../../modules/recognition/locales/it/admin.json");

  translations.ar["recognition.user"] = () =>
    import("../../modules/recognition/locales/ar/user.json");
  translations.ar["recognition.shared"] = () =>
    import("../../modules/recognition/locales/ar/shared.json");
  translations.ar["recognition.admin"] = () =>
    import("../../modules/recognition/locales/ar/admin.json");
}

// Conditionally add chart module
if (import.meta.env.__HAS_CHART__) {
  translations.fa["chart.user"] = () =>
    import("../../modules/chart/locales/fa/user.json");
  translations.fa["chart.shared"] = () =>
    import("../../modules/chart/locales/fa/shared.json");
  translations.fa["chart.admin"] = () =>
    import("../../modules/chart/locales/fa/admin.json");

  translations.en["chart.user"] = () =>
    import("../../modules/chart/locales/en/user.json");
  translations.en["chart.shared"] = () =>
    import("../../modules/chart/locales/en/shared.json");
  translations.en["chart.admin"] = () =>
    import("../../modules/chart/locales/en/admin.json");

  translations.fr["chart.user"] = () =>
    import("../../modules/chart/locales/fr/user.json");
  translations.fr["chart.shared"] = () =>
    import("../../modules/chart/locales/fr/shared.json");
  translations.fr["chart.admin"] = () =>
    import("../../modules/chart/locales/fr/admin.json");

  translations.de["chart.user"] = () =>
    import("../../modules/chart/locales/de/user.json");
  translations.de["chart.shared"] = () =>
    import("../../modules/chart/locales/de/shared.json");
  translations.de["chart.admin"] = () =>
    import("../../modules/chart/locales/de/admin.json");

  translations.es["chart.user"] = () =>
    import("../../modules/chart/locales/es/user.json");
  translations.es["chart.shared"] = () =>
    import("../../modules/chart/locales/es/shared.json");
  translations.es["chart.admin"] = () =>
    import("../../modules/chart/locales/es/admin.json");

  translations.it["chart.user"] = () =>
    import("../../modules/chart/locales/it/user.json");
  translations.it["chart.shared"] = () =>
    import("../../modules/chart/locales/it/shared.json");
  translations.it["chart.admin"] = () =>
    import("../../modules/chart/locales/it/admin.json");

  translations.ar["chart.user"] = () =>
    import("../../modules/chart/locales/ar/user.json");
  translations.ar["chart.shared"] = () =>
    import("../../modules/chart/locales/ar/shared.json");
  translations.ar["chart.admin"] = () =>
    import("../../modules/chart/locales/ar/admin.json");
}

// Conditionally add performance module
if (import.meta.env.__HAS_PERFORMANCE__) {
  translations.fa["performance.user"] = () =>
    import("../../modules/performance/locales/fa/user.json");
  translations.fa["performance.shared"] = () =>
    import("../../modules/performance/locales/fa/shared.json");
  translations.fa["performance.admin"] = () =>
    import("../../modules/performance/locales/fa/admin.json");

  translations.en["performance.user"] = () =>
    import("../../modules/performance/locales/en/user.json");
  translations.en["performance.shared"] = () =>
    import("../../modules/performance/locales/en/shared.json");
  translations.en["performance.admin"] = () =>
    import("../../modules/performance/locales/en/admin.json");

  translations.fr["performance.user"] = () =>
    import("../../modules/performance/locales/fr/user.json");
  translations.fr["performance.shared"] = () =>
    import("../../modules/performance/locales/fr/shared.json");
  translations.fr["performance.admin"] = () =>
    import("../../modules/performance/locales/fr/admin.json");

  translations.de["performance.user"] = () =>
    import("../../modules/performance/locales/de/user.json");
  translations.de["performance.shared"] = () =>
    import("../../modules/performance/locales/de/shared.json");
  translations.de["performance.admin"] = () =>
    import("../../modules/performance/locales/de/admin.json");

  translations.es["performance.user"] = () =>
    import("../../modules/performance/locales/es/user.json");
  translations.es["performance.shared"] = () =>
    import("../../modules/performance/locales/es/shared.json");
  translations.es["performance.admin"] = () =>
    import("../../modules/performance/locales/es/admin.json");

  translations.it["performance.user"] = () =>
    import("../../modules/performance/locales/it/user.json");
  translations.it["performance.shared"] = () =>
    import("../../modules/performance/locales/it/shared.json");
  translations.it["performance.admin"] = () =>
    import("../../modules/performance/locales/it/admin.json");

  translations.ar["performance.user"] = () =>
    import("../../modules/performance/locales/ar/user.json");
  translations.ar["performance.shared"] = () =>
    import("../../modules/performance/locales/ar/shared.json");
  translations.ar["performance.admin"] = () =>
    import("../../modules/performance/locales/ar/admin.json");
}

// Conditionally add application module
if (import.meta.env.__HAS_APPLICATION__) {
  translations.fa["application.user"] = () =>
    import("../../modules/application/locales/fa/user.json");
  translations.fa["application.shared"] = () =>
    import("../../modules/application/locales/fa/shared.json");
  translations.fa["application.admin"] = () =>
    import("../../modules/application/locales/fa/admin.json");

  translations.en["application.user"] = () =>
    import("../../modules/application/locales/en/user.json");
  translations.en["application.shared"] = () =>
    import("../../modules/application/locales/en/shared.json");
  translations.en["application.admin"] = () =>
    import("../../modules/application/locales/en/admin.json");

  translations.fr["application.user"] = () =>
    import("../../modules/application/locales/fr/user.json");
  translations.fr["application.shared"] = () =>
    import("../../modules/application/locales/fr/shared.json");
  translations.fr["application.admin"] = () =>
    import("../../modules/application/locales/fr/admin.json");

  translations.de["application.user"] = () =>
    import("../../modules/application/locales/de/user.json");
  translations.de["application.shared"] = () =>
    import("../../modules/application/locales/de/shared.json");
  translations.de["application.admin"] = () =>
    import("../../modules/application/locales/de/admin.json");

  translations.es["application.user"] = () =>
    import("../../modules/application/locales/es/user.json");
  translations.es["application.shared"] = () =>
    import("../../modules/application/locales/es/shared.json");
  translations.es["application.admin"] = () =>
    import("../../modules/application/locales/es/admin.json");

  translations.it["application.user"] = () =>
    import("../../modules/application/locales/it/user.json");
  translations.it["application.shared"] = () =>
    import("../../modules/application/locales/it/shared.json");
  translations.it["application.admin"] = () =>
    import("../../modules/application/locales/it/admin.json");

  translations.ar["application.user"] = () =>
    import("../../modules/application/locales/ar/user.json");
  translations.ar["application.shared"] = () =>
    import("../../modules/application/locales/ar/shared.json");
  translations.ar["application.admin"] = () =>
    import("../../modules/application/locales/ar/admin.json");
}

// Conditionally add handbook module
if (import.meta.env.__HAS_HANDBOOK__) {
  translations.fa["handbook.user"] = () =>
    import("../../modules/handbook/locales/fa/user.json");
  translations.fa["handbook.shared"] = () =>
    import("../../modules/handbook/locales/fa/shared.json");
  translations.fa["handbook.admin"] = () =>
    import("../../modules/handbook/locales/fa/admin.json");

  translations.en["handbook.user"] = () =>
    import("../../modules/handbook/locales/en/user.json");
  translations.en["handbook.shared"] = () =>
    import("../../modules/handbook/locales/en/shared.json");
  translations.en["handbook.admin"] = () =>
    import("../../modules/handbook/locales/en/admin.json");

  translations.fr["handbook.user"] = () =>
    import("../../modules/handbook/locales/fr/user.json");
  translations.fr["handbook.shared"] = () =>
    import("../../modules/handbook/locales/fr/shared.json");
  translations.fr["handbook.admin"] = () =>
    import("../../modules/handbook/locales/fr/admin.json");

  translations.de["handbook.user"] = () =>
    import("../../modules/handbook/locales/de/user.json");
  translations.de["handbook.shared"] = () =>
    import("../../modules/handbook/locales/de/shared.json");
  translations.de["handbook.admin"] = () =>
    import("../../modules/handbook/locales/de/admin.json");

  translations.es["handbook.user"] = () =>
    import("../../modules/handbook/locales/es/user.json");
  translations.es["handbook.shared"] = () =>
    import("../../modules/handbook/locales/es/shared.json");
  translations.es["handbook.admin"] = () =>
    import("../../modules/handbook/locales/es/admin.json");

  translations.it["handbook.user"] = () =>
    import("../../modules/handbook/locales/it/user.json");
  translations.it["handbook.shared"] = () =>
    import("../../modules/handbook/locales/it/shared.json");
  translations.it["handbook.admin"] = () =>
    import("../../modules/handbook/locales/it/admin.json");

  translations.ar["handbook.user"] = () =>
    import("../../modules/handbook/locales/ar/user.json");
  translations.ar["handbook.shared"] = () =>
    import("../../modules/handbook/locales/ar/shared.json");
  translations.ar["handbook.admin"] = () =>
    import("../../modules/handbook/locales/ar/admin.json");
}

// Conditionally add news module
if (import.meta.env.__HAS_NEWS__) {
  translations.fa["news.user"] = () =>
    import("../../modules/news/locales/fa/user.json");
  translations.fa["news.shared"] = () =>
    import("../../modules/news/locales/fa/shared.json");
  translations.fa["news.admin"] = () =>
    import("../../modules/news/locales/fa/admin.json");

  translations.en["news.user"] = () =>
    import("../../modules/news/locales/en/user.json");
  translations.en["news.shared"] = () =>
    import("../../modules/news/locales/en/shared.json");
  translations.en["news.admin"] = () =>
    import("../../modules/news/locales/en/admin.json");

  translations.fr["news.user"] = () =>
    import("../../modules/news/locales/fr/user.json");
  translations.fr["news.shared"] = () =>
    import("../../modules/news/locales/fr/shared.json");
  translations.fr["news.admin"] = () =>
    import("../../modules/news/locales/fr/admin.json");

  translations.de["news.user"] = () =>
    import("../../modules/news/locales/de/user.json");
  translations.de["news.shared"] = () =>
    import("../../modules/news/locales/de/shared.json");
  translations.de["news.admin"] = () =>
    import("../../modules/news/locales/de/admin.json");

  translations.es["news.user"] = () =>
    import("../../modules/news/locales/es/user.json");
  translations.es["news.shared"] = () =>
    import("../../modules/news/locales/es/shared.json");
  translations.es["news.admin"] = () =>
    import("../../modules/news/locales/es/admin.json");

  translations.it["news.user"] = () =>
    import("../../modules/news/locales/it/user.json");
  translations.it["news.shared"] = () =>
    import("../../modules/news/locales/it/shared.json");
  translations.it["news.admin"] = () =>
    import("../../modules/news/locales/it/admin.json");

  translations.ar["news.user"] = () =>
    import("../../modules/news/locales/ar/user.json");
  translations.ar["news.shared"] = () =>
    import("../../modules/news/locales/ar/shared.json");
  translations.ar["news.admin"] = () =>
    import("../../modules/news/locales/ar/admin.json");
}

// Conditionally add mood module
if (import.meta.env.__HAS_MOOD__) {
  translations.fa["mood.user"] = () =>
    import("../../modules/mood/locales/fa/user.json");
  translations.fa["mood.shared"] = () =>
    import("../../modules/mood/locales/fa/shared.json");
  translations.fa["mood.admin"] = () =>
    import("../../modules/mood/locales/fa/admin.json");

  translations.en["mood.user"] = () =>
    import("../../modules/mood/locales/en/user.json");
  translations.en["mood.shared"] = () =>
    import("../../modules/mood/locales/en/shared.json");
  translations.en["mood.admin"] = () =>
    import("../../modules/mood/locales/en/admin.json");

  translations.fr["mood.user"] = () =>
    import("../../modules/mood/locales/fr/user.json");
  translations.fr["mood.shared"] = () =>
    import("../../modules/mood/locales/fr/shared.json");
  translations.fr["mood.admin"] = () =>
    import("../../modules/mood/locales/fr/admin.json");

  translations.de["mood.user"] = () =>
    import("../../modules/mood/locales/de/user.json");
  translations.de["mood.shared"] = () =>
    import("../../modules/mood/locales/de/shared.json");
  translations.de["mood.admin"] = () =>
    import("../../modules/mood/locales/de/admin.json");

  translations.es["mood.user"] = () =>
    import("../../modules/mood/locales/es/user.json");
  translations.es["mood.shared"] = () =>
    import("../../modules/mood/locales/es/shared.json");
  translations.es["mood.admin"] = () =>
    import("../../modules/mood/locales/es/admin.json");

  translations.it["mood.user"] = () =>
    import("../../modules/mood/locales/it/user.json");
  translations.it["mood.shared"] = () =>
    import("../../modules/mood/locales/it/shared.json");
  translations.it["mood.admin"] = () =>
    import("../../modules/mood/locales/it/admin.json");

  translations.ar["mood.user"] = () =>
    import("../../modules/mood/locales/ar/user.json");
  translations.ar["mood.shared"] = () =>
    import("../../modules/mood/locales/ar/shared.json");
  translations.ar["mood.admin"] = () =>
    import("../../modules/mood/locales/ar/admin.json");
}

// Conditionally add messenger module
if (import.meta.env.__HAS_MESSENGER__) {
  translations.fa["messenger.user"] = () =>
    import("../../modules/messenger/locales/fa/user.json");
  translations.fa["messenger.shared"] = () =>
    import("../../modules/messenger/locales/fa/shared.json");
  translations.fa["messenger.admin"] = () =>
    import("../../modules/messenger/locales/fa/admin.json");

  translations.en["messenger.user"] = () =>
    import("../../modules/messenger/locales/en/user.json");
  translations.en["messenger.shared"] = () =>
    import("../../modules/messenger/locales/en/shared.json");
  translations.en["messenger.admin"] = () =>
    import("../../modules/messenger/locales/en/admin.json");

  translations.fr["messenger.user"] = () =>
    import("../../modules/messenger/locales/fr/user.json");
  translations.fr["messenger.shared"] = () =>
    import("../../modules/messenger/locales/fr/shared.json");
  translations.fr["messenger.admin"] = () =>
    import("../../modules/messenger/locales/fr/admin.json");

  translations.de["messenger.user"] = () =>
    import("../../modules/messenger/locales/de/user.json");
  translations.de["messenger.shared"] = () =>
    import("../../modules/messenger/locales/de/shared.json");
  translations.de["messenger.admin"] = () =>
    import("../../modules/messenger/locales/de/admin.json");

  translations.es["messenger.user"] = () =>
    import("../../modules/messenger/locales/es/user.json");
  translations.es["messenger.shared"] = () =>
    import("../../modules/messenger/locales/es/shared.json");
  translations.es["messenger.admin"] = () =>
    import("../../modules/messenger/locales/es/admin.json");

  translations.it["messenger.user"] = () =>
    import("../../modules/messenger/locales/it/user.json");
  translations.it["messenger.shared"] = () =>
    import("../../modules/messenger/locales/it/shared.json");
  translations.it["messenger.admin"] = () =>
    import("../../modules/messenger/locales/it/admin.json");

  translations.ar["messenger.user"] = () =>
    import("../../modules/messenger/locales/ar/user.json");
  translations.ar["messenger.shared"] = () =>
    import("../../modules/messenger/locales/ar/shared.json");
  translations.ar["messenger.admin"] = () =>
    import("../../modules/messenger/locales/ar/admin.json");
}

// Conditionally add meet module
if (import.meta.env.__HAS_MEET__) {
  translations.fa["meet.user"] = () =>
    import("../../modules/meet/locales/fa/user.json");
  translations.fa["meet.shared"] = () =>
    import("../../modules/meet/locales/fa/shared.json");
  translations.fa["meet.admin"] = () =>
    import("../../modules/meet/locales/fa/admin.json");

  translations.en["meet.user"] = () =>
    import("../../modules/meet/locales/en/user.json");
  translations.en["meet.shared"] = () =>
    import("../../modules/meet/locales/en/shared.json");
  translations.en["meet.admin"] = () =>
    import("../../modules/meet/locales/en/admin.json");

  translations.fr["meet.user"] = () =>
    import("../../modules/meet/locales/fr/user.json");
  translations.fr["meet.shared"] = () =>
    import("../../modules/meet/locales/fr/shared.json");
  translations.fr["meet.admin"] = () =>
    import("../../modules/meet/locales/fr/admin.json");

  translations.de["meet.user"] = () =>
    import("../../modules/meet/locales/de/user.json");
  translations.de["meet.shared"] = () =>
    import("../../modules/meet/locales/de/shared.json");
  translations.de["meet.admin"] = () =>
    import("../../modules/meet/locales/de/admin.json");

  translations.es["meet.user"] = () =>
    import("../../modules/meet/locales/es/user.json");
  translations.es["meet.shared"] = () =>
    import("../../modules/meet/locales/es/shared.json");
  translations.es["meet.admin"] = () =>
    import("../../modules/meet/locales/es/admin.json");

  translations.it["meet.user"] = () =>
    import("../../modules/meet/locales/it/user.json");
  translations.it["meet.shared"] = () =>
    import("../../modules/meet/locales/it/shared.json");
  translations.it["meet.admin"] = () =>
    import("../../modules/meet/locales/it/admin.json");

  translations.ar["meet.user"] = () =>
    import("../../modules/meet/locales/ar/user.json");
  translations.ar["meet.shared"] = () =>
    import("../../modules/meet/locales/ar/shared.json");
  translations.ar["meet.admin"] = () =>
    import("../../modules/meet/locales/ar/admin.json");
}

// Conditionally add report module
if (import.meta.env.__HAS_REPORT__) {
  translations.fa["report.user"] = () =>
    import("../../modules/report/locales/fa/user.json");
  translations.fa["report.shared"] = () =>
    import("../../modules/report/locales/fa/shared.json");
  translations.fa["report.admin"] = () =>
    import("../../modules/report/locales/fa/admin.json");

  translations.en["report.user"] = () =>
    import("../../modules/report/locales/en/user.json");
  translations.en["report.shared"] = () =>
    import("../../modules/report/locales/en/shared.json");
  translations.en["report.admin"] = () =>
    import("../../modules/report/locales/en/admin.json");

  translations.fr["report.user"] = () =>
    import("../../modules/report/locales/fr/user.json");
  translations.fr["report.shared"] = () =>
    import("../../modules/report/locales/fr/shared.json");
  translations.fr["report.admin"] = () =>
    import("../../modules/report/locales/fr/admin.json");

  translations.de["report.user"] = () =>
    import("../../modules/report/locales/de/user.json");
  translations.de["report.shared"] = () =>
    import("../../modules/report/locales/de/shared.json");
  translations.de["report.admin"] = () =>
    import("../../modules/report/locales/de/admin.json");

  translations.es["report.user"] = () =>
    import("../../modules/report/locales/es/user.json");
  translations.es["report.shared"] = () =>
    import("../../modules/report/locales/es/shared.json");
  translations.es["report.admin"] = () =>
    import("../../modules/report/locales/es/admin.json");

  translations.it["report.user"] = () =>
    import("../../modules/report/locales/it/user.json");
  translations.it["report.shared"] = () =>
    import("../../modules/report/locales/it/shared.json");
  translations.it["report.admin"] = () =>
    import("../../modules/report/locales/it/admin.json");

  translations.ar["report.user"] = () =>
    import("../../modules/report/locales/ar/user.json");
  translations.ar["report.shared"] = () =>
    import("../../modules/report/locales/ar/shared.json");
  translations.ar["report.admin"] = () =>
    import("../../modules/report/locales/ar/admin.json");
}

i18n
  .use(initReactI18next)
  .use({
    type: "backend",
    read(language: string, namespace: string, callback: ReadCallback) {
      const [module, _namespace] = namespace.split(".");

      const key = _namespace ? `${module}.${_namespace}` : module;

      translations[language]?.[key]?.()
        .then((resources) => {
          callback(null, resources.default);
        })
        .catch((error) => {
          callback(error, null);
        });
    },
  })
  //   .use(
  //     resourcesToBackend((language: string, namespace: string) => {
  //       const [module, _namespace = "translation"] = namespace.split(".");
  //       return import(
  //         `../..../../modules/${module}/locales/${language}/${_namespace}.json`
  //       ).catch(() => ({}));
  //     }),
  //   )
  .init({
    lng: "fa",
    fallbackLng: false,
    ns: Object.keys(translations.fa),
    defaultNS: "core.shared",

    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  })
  .then(() => {
    z.setErrorMap(makeZodI18nMap({ ns: "core.zod" }));
  });

export default i18n;
